import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../core/services/core.translate.service';
import { TourService } from '../../core/modules/tour/tour.service';
import { TrackerRest } from './services/tracker.rest';
import { TrackerService } from './services/tracker.service';
import { TrackeritemService } from './services/trackeritem.service';
import { TrackeritemRest } from './services/trackeritem.rest';
import { TrackerQueryService } from './services/tracker.query.service';
import { AccessDeniedService } from '../../core/services/core.access-denied.service';

@Injectable()
export class TrackerResolvesService {

    constructor(
        private stateService: StateService,
        private translateService: GfTranslateService,
        private tourService: TourService,
        private trackerRest: TrackerRest,
        private trackerService: TrackerService,
        private trackeritemService: TrackeritemService,
        private trackeritemRest: TrackeritemRest,
        private trackerQueryService: TrackerQueryService,
        private accessDeniedService: AccessDeniedService
    ) {
    }

    // Home tracker route - fetches tracker and redirects
    async getHomepageTracker(trackerId: string, transition: any): Promise<any> {
        try {
            const data = await this.trackerRest.fetch(parseInt(trackerId), '', {});
            const _params = { unixName: (data as any).project.unixName, tracker: (data as any).id };
            await this.stateService.go('home.project.summary.tracker.browse', _params);
            return data;
        } catch (response) {
            this.accessDeniedService.goRefused(transition, {
                message: this.translateService.translate(
                    "Sorry, that Tracker does not exist, or you are not allowed to view it."
                )
            });
            throw response;
        }
    }

    // Tracker item basic resolve - fetches item and redirects if needed
    async getTrackerItemBasic(itemId: string, unixName: string, trackerId: string, transition: any): Promise<any> {
        try {
            const data = await this.trackeritemService.fetch(itemId, {});
            
            //Check if the TI belongs to a different project
            if (unixName != (data as any).tracker.project.unixName ||
                _.get(trackerId, 0) != _.get(data, 'tracker.id', -1)
            ) {
                const _params = { unixName: (data as any).tracker.project.unixName, id: (data as any).id, tracker: (data as any).tracker.id };
                this.stateService.go('home.project.summary.tracker.item.edit', _params, {
                    notify: true,
                    // ensure reload of the current state
                    reload: true,
                    inherit: false
                });
            }
            return data;
        } catch (response) {
            this.accessDeniedService.goRefused(transition, {
                message: this.translateService.translate(
                    "Sorry, that Tracker Item does not exist, or you are not allowed to view it."
                )
            });
            throw response;
        }
    }

    // Set ACLs for tracker operations
    setAcls(project: any) {
        this.trackerService.acl.set(_.get(project, 'rel.permissions.trackers', []));
    }

    // Get tracker details
    getTracker(stateParams: any) {
        return this.trackerService.getTracker(stateParams, null);
    }

    // Get query details
    getQuery(trackerId: string, queryId: string) {
        return this.trackerRest.query(parseInt(trackerId), parseInt(queryId));
    }

    // Create new empty query
    getNewQuery() {
        return {
            id: 0,
            queryFields: [
                { type: "5", id: 0, typeName: "FIELD_SORTCOL", value: "id" },
                { type: "6", id: 0, typeName: "FIELD_SORTORD", value: "ASC" }
            ],
            queryDisplayColumns: []
        };
    }

    // Security check - can read tracker
    securityCanRead(trackerId: string) {
        return this.trackerService.acl.assertPermission(this.trackerService.acl.canRead({ id: trackerId }));
    }

    // Security check - can submit to tracker
    securityCanSubmit(trackerId: string) {
        return this.trackerService.acl.assertPermission(this.trackerService.acl.canSubmit({ id: trackerId }));
    }

    // Get project queries
    getProjectQueries(trackerId: string, project: any) {
        return this.trackerQueryService.getProjectQueries(
            project.id,
            _.get(project, 'rel.permissions.trackers', {}),
            trackerId
        );
    }

    // Get tracker with project validation and redirection
    async getTrackerWithValidation(unixName: string, trackerId: string, queries: any): Promise<any> {
        try {
            const stateParams = { unixName, tracker: trackerId };
            const tracker = await this.trackerService.getTracker(stateParams, queries);
            
            //If the tracker belongs to a different project, then redirect to that project
            if ((tracker as any).project && (tracker as any).project.unixName !== unixName) {
                this.stateService.go('.', { unixName: (tracker as any).project.unixName, tracker: trackerId });
            }
            return tracker;
        } catch (error) {
            throw error;
        }
    }

    // Get query preference for tracker
    getQueryPreference(stateParams: any) {
        const trackerId = stateParams.tracker;
        const queryId = stateParams.query || '';
        return this.trackerQueryService.getQueryPreferenceForTracker(trackerId, queryId);
    }

    // Get tracker item ACLs
    getTrackerItemAcls(project: any, trackeritem: any) {
        this.trackerService.acl.set(_.get(project, 'rel.permissions.trackers', []));
        return this.trackeritemService.getAcls(project, trackeritem.tracker.id, trackeritem);
    }

    // Get tracker item details
    async getTrackerItemDetails(itemId: string, transition: any): Promise<any> {
        try {
            const data = await this.trackeritemRest.getDetails(parseInt(itemId), { rel: "assignees,attachments,field_elements,monitoring,merge_status" });
            (data as any).followup = '';
            (data as any).lockStatus = (data as any).lockStatus ?? 0;
            return data;
        } catch (response) {
            this.accessDeniedService.goRefused(transition, {
                message: this.translateService.translate(
                    "Sorry, that Tracker Item does not exist, or you are not allowed to view it."
                )
            });
            throw response;
        }
    }

    // Get tracker item with commits
    getTrackerItemWithCommits(trackeritem: any) {
        return new Promise((resolve, reject) => {
            if (trackeritem.rel.commits) {
                resolve(trackeritem);
                return;
            }

            this.trackeritemRest.getDetails(trackeritem.id, { rel: 'commits'}).then(
                (data) => {
                    trackeritem.rel.commits = _.get(data, 'rel.commits', []);
                    resolve(trackeritem);
                },
                (result) => reject(result)
            );    
        });
    }

    // Get tracker item merge info
    getTrackerItemMerge(trackeritem: any) {
        return this.trackeritemService.getTrackeritemMerge(trackeritem);
    }

    // Get tracker item with related items
    getTrackerItemWithRelated(trackeritem: any) {
        return new Promise((resolve, reject) => {
            if (trackeritem.rel.dependencies && trackeritem.rel.duplicates) {
                resolve(trackeritem);
                return;
            }

            this.trackeritemRest.getDetails(trackeritem.id, { rel: 'dependencies,duplicates,associations'}).then(
                (data) => {
                    trackeritem.rel.associations = _.get(data, 'rel.associations', []);
                    trackeritem.rel.dependencies = _.get(data, 'rel.dependencies', []);
                    trackeritem.rel.duplicates = _.get(data, 'rel.duplicates', []);
                    resolve(trackeritem);
                },
                (result) => reject(result)
            );    
        });
    }

    // Get tracker item with children (#65237 Breakdown tab), mirroring getTrackerItemWithRelated
    getTrackerItemWithChildren(trackeritem: any) {
        return new Promise((resolve, reject) => {
            if (trackeritem.rel.children) {
                resolve(trackeritem);
                return;
            }

            this.trackeritemRest.getDetails(trackeritem.id, { rel: 'children'}).then(
                (data) => {
                    trackeritem.rel.children = _.get(data, 'rel.children', []);
                    trackeritem.rel.remaining = _.get(data, 'rel.remaining', []);
                    trackeritem.rel.burndown = _.get(data, 'rel.burndown', {});
                    resolve(trackeritem);
                },
                (result) => reject(result)
            );
        });
    }

    // Tour setup for tracker item add
    getTrackerItemAddTour() {
        return this.tourService.setTour('trackeritemAddCtrl');
    }

    // Tour setup for tracker admin
    getTrackerAdminTour() {
        return this.tourService.setTour('projectTrackerAdminCtrl');
    }
}
